import { useToast } from "components/toast";
import React, { useState, useContext } from "react";
import H5 from "components/typography/H5";
import { Box, Flex, Grid } from "theme-ui";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import { SubmitIcon } from "components/svg";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import Span from "components/typography/Span";
import { generateQuoteFromEstimate } from "api/QuoteRequests";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
// RC5 (Session 55): the provider/price controls moved OUT of this file
// into their own component so the Work Order case file can mount the
// same controls instead of linking here. Behavior is unchanged.
import ProviderPriceControls from "./ProviderPriceControls";

const Items = ({ rfq, isCompany, nteAmount = null, onChanged }: { rfq: any; isCompany: boolean; nteAmount?: number | null; onChanged?: () => void }) => {
  const [submittingRowId, setSubmittingRowId] = useState<string | null>(null);
  const toast = useToast();
  // The RFQ's live quote (newest that is not denied / expired / superseded).
  // The page used to read `rfq.quote`, which the API never sends, so
  // "Generate Quote" showed on RFQs that were already quoted - and the
  // server then refused it silently (2026-10-08, GMR-00100).
  const rfqQuote: any = (rfq?.quotes ?? [])
    .filter((q: any) => !q?.deletedAt && ![2, 4, 5].includes(Number(q?.status)))
    .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null;
  const quoteLabel = (q: any) => `GMQ-${String(q?.sequence ?? "").padStart(5, "0")}${q?.versionDisplay && q.versionDisplay !== "1" && q.versionDisplay !== "1.0" ? `-${q.versionDisplay}` : ""}`;
  const columnHelper = createColumnHelper();
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);


  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
  };

  const calculateTotalPrice = (service: any) => {
    const mainServiceTotal = service.quantity * parseFloat(service.price);
    const addonsTotal = service.addons.reduce((sum: number, addon: any) => sum + (addon.quantity > 0 ? addon.quantity * parseFloat(addon.price) : 0), 0);
    return formatCurrency(mainServiceTotal + addonsTotal);
  };

  const calculateTotalCost = (service: any) => {
    const mainServiceTotal = service.quantity * parseFloat(service.cost);
    const addonsTotal = service.addons.reduce((sum: number, addon: any) => sum + (addon.quantity > 0 ? addon.quantity * parseFloat(addon.baseCost) : 0), 0);
    const totalCost = mainServiceTotal + addonsTotal;
    return formatCurrency(totalCost);
  };

  const calculateDiscountPrice = (service: any, discountPercent: number) => {
    const mainServiceTotal = service.quantity * parseFloat(service.price);
    const addonsTotal = service.addons.reduce((sum: number, addon: any) => sum + (addon.quantity > 0 ? addon.quantity * parseFloat(addon.price) : 0), 0);
    const total = mainServiceTotal + addonsTotal;
    const discountTotal = total * (1 - discountPercent / 100);
    return formatCurrency(discountTotal);
  };

  const _generateQuote = async (serviceId: string) => {
    setSubmittingRowId(serviceId);
    console.log("Generating quote for service ID:", serviceId);
    const service = {
      selectServices: [serviceId],
    };

    const res = await generateQuoteFromEstimate({ data: { id: rfq.estimate.id }, service });

    if (res.id) {
      // Track conversion to quote
      void trackActivity({
        module: "quote-requests",
        action: "convert_to_quote",
        actionType: "click",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote-request",
        entityId: rfq.id,
        metadata: {
          quoteRequestId: rfq.id,
          quoteId: res.id,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      
      window.open(`/quotes/${res.id}`, "_blank");
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      toast.add(res?.message || "The quote could not be generated.", "var(--theme-ui-colors-danger)");
    }

    setSubmittingRowId(null);
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "id",
      cell: (info: any) => (
        <Span color="#000" fontWeight="500">
          {info.getValue()}
        </Span>
      ),
      header: () => <span>Name</span>,
      meta: {
        style: { width: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.quantity, {
      id: "quantity",
      cell: (info: any) => {
        const quantity = info.getValue();
        const unit = info.row.original?.unit;
        return quantity !== undefined ? `${quantity} ${unit || ""}`.trim() : "";
      },
      header: () => <span>Quantity</span>,
    }),
    ...(isCompany
      ? [
          columnHelper.accessor((row: any) => row?.markup, {
            id: "markup",
            cell: (info: any) => `${info.getValue()}%`,
            header: () => <span>Markup</span>,
          }),
        ]
      : []),
    ...(isCompany
      ? [
          columnHelper.accessor((row: any) => row?.baseCost, {
            id: "baseCost",
            cell: (info: any) => `$${info.getValue()}`,
            header: () => <span>Cost Per Unit</span>,
          }),
        ]
      : []),
    columnHelper.accessor((row: any) => row?.price, {
      id: "price",
      cell: (info: any) => `$${info.getValue()}`,
      header: () => <span>Price Per Unit</span>,
    }),
    ...(isCompany
      ? [
          columnHelper.accessor((row: any) => row?.baseCost * row?.quantity, {
            id: "totalCost",
            cell: (info: any) => formatCurrency(info.getValue()),
            header: () => <span>Total Cost</span>,
          }),
        ]
      : []),
    columnHelper.accessor((row: any) => row?.price * row?.quantity, {
      id: "totalPrice",
      cell: (info: any) => formatCurrency(info.getValue()),
      header: () => <span>Total Price</span>,
    }),
  ];

  return (
    <>
      <H5 fontFamily="Rubik" fontSize="16px" marginLeft="0px" marginTop="0px">
        Items
      </H5>
      {rfq.estimate.selectedService ? (
        <Flex sx={{ flexDirection: "column", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07);" }}>
          <Flex sx={{ flexDirection: "row", padding: "15px 20px 15px 20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
            <Flex sx={{ flex: 1, alignItems: "center" }}>
              <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000", marginRight: "5px" }}>{rfq.estimate.selectedService.product.name}</Box>
              <Box>-</Box>
              <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "500", color: themevals.colors.dkgrey, marginLeft: "5px" }}>{rfq.estimate.selectedService.name}</Box>
            </Flex>
            <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
              {!rfqQuote && (
                <BasicButton
                  type="button"
                  $submitting={submittingRowId === rfq.estimate.selectedService.id}
                  styles={{ margin: "0 0 0 10px" }}
                  $iconHeight="14px"
                  $iconWidth="14px"
                  $loadingColor="#fff"
                  keepText={true}
                  onClick={() => _generateQuote(rfq.estimate.selectedService.id)}
                >
                  {submittingRowId !== rfq.estimate.selectedService.id && <SubmitIcon fill={"#fff"} direction="up" />}
                  Generate Quote
                </BasicButton>
              )}
              {rfqQuote && (
                <Flex sx={{ alignItems: "center", justifyContent: "flex-end", gap: "20px" }}>
                  <Flex
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      alignItems: "center",
                      fontWeight: "600",
                      height: "36px",
                      color: themevals.colors.success,
                    }}
                  >
                    {`Quote generated on ${moment(rfqQuote.createdAt).format("MMM D, YYYY")}.`}
                  </Flex>
                  <Flex
                    onClick={() => navigate(`/quotes/${rfqQuote.id}`)}
                    sx={{
                      fontFamily: "Poppins",
                      background: themevals.colors.success,
                      alignItems: "center",
                      padding: "0 15px",
                      borderRadius: "50px",
                      color: "white",
                      fontSize: "12px",
                      height: "36px",
                      cursor: "pointer",
                    }}
                  >{`Quote# ${quoteLabel(rfqQuote)}`}</Flex>
                </Flex>
              )}
            </Flex>
          </Flex>
          <Grid gap={0} columns={[1, isCompany ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr 1fr"]} sx={{ padding: "20px" }}>
            <Flex sx={{ flexDirection: "column" }}>
              <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Quantity</Box>
              <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>
                {rfq.estimate.selectedService.quantity} {rfq.estimate.selectedService.product.unit}
              </Box>
            </Flex>
            {isCompany && (
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Markup</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{rfq.estimate.selectedService.markup}%</Box>
              </Flex>
            )}
            {isCompany && (
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Bulk Product Cost Per Unit</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>${rfq.estimate.selectedService.cost}</Box>
              </Flex>
            )}
            <Flex sx={{ flexDirection: "column" }}>
              <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Bulk Product Price Per Unit</Box>
              <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>${rfq.estimate.selectedService.price}</Box>
            </Flex>
            {!isCompany && (
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Grand Total Price</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalPrice(rfq.estimate.selectedService)}</Box>
              </Flex>
            )}
            {!isCompany && (
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.success, marginBottom: "8px", lineHeight: "12px" }}>Pay 50% deposit for 3% discount</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: themevals.colors.success }}>{calculateDiscountPrice(rfq.estimate.selectedService, 3)}</Box>
              </Flex>
            )}
            {!isCompany && (
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.success, marginBottom: "8px", lineHeight: "12px" }}>Pay 100% deposit for 5% discount</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: themevals.colors.success }}>{calculateDiscountPrice(rfq.estimate.selectedService, 5)}</Box>
              </Flex>
            )}
          </Grid>

          {isCompany && (
            <Grid gap={0} columns={[1, "1fr 1fr 1fr 1fr"]} sx={{ padding: "20px" }}>
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Grand Total Cost</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalCost(rfq.estimate.selectedService)}</Box>
              </Flex>
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Grand Total Price</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalPrice(rfq.estimate.selectedService)}</Box>
              </Flex>
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Bulk Product Provider</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{rfq.estimate.selectedService.depot.provider.name}</Box>
              </Flex>
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Bulk Product Depot</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{rfq.estimate.selectedService.depot.name}</Box>
              </Flex>
            </Grid>
          )}
          {isCompany && !rfqQuote && onChanged && (
            <ProviderPriceControls
              rfq={rfq}
              service={rfq.estimate.selectedService}
              nteAmount={nteAmount}
              onChanged={onChanged}
            />
          )}
           {isCompany && (
           <Grid gap={0} columns={[1, "1fr 1fr 1fr 1fr"]} sx={{ padding: "20px" }}>
           <Flex sx={{ flexDirection: "column" }}>
           <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.success, marginBottom: "8px", lineHeight: "12px" }}>Pay 50% deposit for 3% discount</Box>
           <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: themevals.colors.success }}>{calculateDiscountPrice(rfq.estimate.selectedService, 3)}</Box>
           </Flex>
             <Flex sx={{ flexDirection: "column" }}>
                 <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.success, marginBottom: "8px", lineHeight: "12px" }}>Pay 100% deposit for 5% discount</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: themevals.colors.success }}>{calculateDiscountPrice(rfq.estimate.selectedService, 5)}</Box>
              </Flex>
            </Grid>
          )}
          {rfq.estimate.selectedService.addons.some((addon: any) => addon.quantity > 0) && (
            <Flex sx={{ flexDirection: "column", padding: "20px" }}>
              <H5 fontFamily="Poppins" fontSize="12px" marginLeft="0px" marginTop="0px" marginBottom="0px" color={themevals.colors.dkgrey}>
                Add-on Products
              </H5>
              <TableBasic
                data={rfq.estimate.selectedService.addons}
                columns={columns}
                enablePaging={false}
                enableSearch={false}
                customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
              />
            </Flex>
          )}
        </Flex>
      ) : (
        rfq.estimate.services.map((service: any, index: number) => (
          <Flex
            sx={{ flexDirection: "column", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07);", marginBottom: "30px" }}
            key={index}
          >
            <Flex sx={{ flexDirection: "row", padding: "15px 20px 15px 20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
              <Flex sx={{ flex: 1, alignItems: "center" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000", marginRight: "5px" }}>{service.product.name}</Box>
                <Box>-</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "500", color: themevals.colors.dkgrey, marginLeft: "5px" }}>{service.name}</Box>
              </Flex>
              <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                {rfqQuote ? (
                  <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 600, color: themevals.colors.success, cursor: "pointer" }} onClick={() => navigate(`/quotes/${rfqQuote.id}`)}>
                    {`Quoted: ${quoteLabel(rfqQuote)} \u2192`}
                  </Box>
                ) : (
                <BasicButton
                  type="button"
                  $submitting={submittingRowId === service.id}
                  styles={{ margin: "0 0 0 10px" }}
                  $iconHeight="14px"
                  $iconWidth="14px"
                  $loadingColor="#fff"
                  keepText={true}
                  onClick={() => _generateQuote(service.id)}
                >
                  {submittingRowId !== service.id && <SubmitIcon fill={"#fff"} direction="up" />}
                  Generate Quote
                </BasicButton>
                )}
              </Flex>
            </Flex>
            <Grid gap={0} columns={[1, isCompany ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr 1fr"]} sx={{ padding: "20px" }}>
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Quantity</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>
                  {service.quantity} {service.product.unit}
                </Box>
              </Flex>
              {isCompany && (
                <Flex sx={{ flexDirection: "column" }}>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Markup</Box>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{service.markup}%</Box>
                </Flex>
              )}
              {isCompany && (
                <Flex sx={{ flexDirection: "column" }}>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Bulk Product Cost Per Unit</Box>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>${service.cost}</Box>
                </Flex>
              )}
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Bulk Product Price Per Unit</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>${service.price}</Box>
              </Flex>
              {!isCompany && (
                <Flex sx={{ flexDirection: "column" }}>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Grand Total Price</Box>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalPrice(service)}</Box>
                </Flex>
              )}
              {!isCompany && (
                <Flex sx={{ flexDirection: "column" }}>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.success, marginBottom: "8px", lineHeight: "12px" }}>Pay 50% deposit for 3% discount</Box>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: themevals.colors.success }}>{calculateDiscountPrice(service, 3)}</Box>
                </Flex>
              )}
              {!isCompany && (
                <Flex sx={{ flexDirection: "column" }}>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.success, marginBottom: "8px", lineHeight: "12px" }}>Pay 100% deposit for 5% discount</Box>
                  <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: themevals.colors.success }}>{calculateDiscountPrice(service, 5)}</Box>
                </Flex>
              )}
            </Grid>

            {isCompany && (
              <Grid gap={0} columns={[1, "1fr 1fr 1fr 1fr"]} sx={{ padding: "20px" }}>
                {isCompany && (
                  <Flex sx={{ flexDirection: "column" }}>
                    <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Grand Total Cost</Box>
                    <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalCost(service)}</Box>
                  </Flex>
                )}
                {isCompany && (
                  <Flex sx={{ flexDirection: "column" }}>
                    <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Grand Total Price</Box>
                    <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalPrice(service)}</Box>
                  </Flex>
                )}
                {isCompany && (
                  <Flex sx={{ flexDirection: "column" }}>
                    <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Bulk Product Provider</Box>
                    <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{service.depot.provider.name}</Box>
                  </Flex>
                )}
                {isCompany && (
                  <Flex sx={{ flexDirection: "column" }}>
                    <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Bulk Product Depot</Box>
                    <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{service.depot.name}</Box>
                  </Flex>
                )}
              </Grid>
            )}
            {isCompany && !rfqQuote && onChanged && (
              <ProviderPriceControls
                rfq={rfq}
                service={service}
                nteAmount={nteAmount}
                onChanged={onChanged}
              />
            )}
             {isCompany && (
            <Grid gap={0} columns={[1, "1fr 1fr 1fr 1fr"]} sx={{ padding: "20px" }}>
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.success, marginBottom: "8px", lineHeight: "12px" }}>Pay 50% deposit for 3% discount</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: themevals.colors.success }}>{calculateDiscountPrice(service, 3)}</Box>
              </Flex>
              <Flex sx={{ flexDirection: "column" }}>
                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.success, marginBottom: "8px", lineHeight: "12px" }}>Pay 100% deposit for 5% discount</Box>
                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: themevals.colors.success }}>{calculateDiscountPrice(service, 5)}</Box>
              </Flex>
            </Grid>
          )}
            {service.addons.some((addon: any) => addon.quantity > 0) && (
              <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                <H5 fontFamily="Poppins" fontSize="12px" marginLeft="0px" marginTop="0px" marginBottom="0px" color={themevals.colors.dkgrey}>
                  Add-on Products
                </H5>
                <TableBasic data={service.addons} columns={columns} enablePaging={false} enableSearch={false} customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }} />
              </Flex>
            )}
          </Flex>
        ))
      )}
    </>
  );
};

export default Items;
